import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Mail, Lock, Eye, EyeOff, User, Phone, ArrowLeft, KeyRound } from 'lucide-react';
import axios from 'axios';
import { API, setAuthToken, syncAuthBackendSignature } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_imageblaze/artifacts/5e5w021j_7D777DBC-744F-44B3-9C70-7AE1E6146D31.png";
// Illustration 3D moderne pour la page de connexion
const BACKGROUND_3D_IMAGE = "https://static.prod-images.emergentagent.com/jobs/a1406132-a684-48c9-924d-9e1842969519/images/078d58e6a92184f81135d2b54d45acc6bc513aa78245bd9d2de67ad7c54b2b5d.png";

// Style du background responsive avec illustration 3D
const backgroundStyle = {
  backgroundImage: `url(${BACKGROUND_3D_IMAGE})`,
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat'
};

const LoginPage = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [authMode, setAuthMode] = useState('login'); // 'login', 'register', 'forgot-password', 'reset-password'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    resetCode: '',
    newPassword: ''
  });

  useEffect(() => {
    syncAuthBackendSignature();

    // Check if already authenticated
    const checkExistingSession = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers
        });
        if (response.data) {
          const user = response.data;
          if (user.role === 'admin') {
            navigate('/admin', { replace: true });
          } else if (user.status === 'approved') {
            navigate('/dashboard', { replace: true });
          } else if (!user.first_name || !user.last_name || !user.phone) {
            navigate('/complete-registration', { replace: true });
          } else {
            navigate('/pending-approval', { replace: true });
          }
        }
      } catch (error) {
        // Retry once without cookies to avoid stale-cookie loop after redeploy
        try {
          const token = localStorage.getItem('session_token');
          if (!token) throw error;
          const retry = await axios.get(`${API}/auth/me`, {
            withCredentials: false,
            headers: { Authorization: `Bearer ${token}` }
          });
          if (retry.data) {
            const user = retry.data;
            if (user.role === 'admin') {
              navigate('/admin', { replace: true });
            } else if (user.status === 'approved') {
              navigate('/dashboard', { replace: true });
            } else if (!user.first_name || !user.last_name || !user.phone) {
              navigate('/complete-registration', { replace: true });
            } else {
              navigate('/pending-approval', { replace: true });
            }
          }
        } catch (retryError) {
          // Not authenticated, stay on login page
        }
      } finally {
        setChecking(false);
      }
    };

    checkExistingSession();
  }, [navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    const authUrl = process.env.REACT_APP_AUTH_URL;
    if (!authUrl) {
      setError('Configuration d\'authentification manquante. Contactez l\'administrateur.');
      return;
    }
    window.location.href = `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const endpoint = authMode === 'login' ? '/auth/email/login' : '/auth/email/register';
      const payload = authMode === 'login' 
        ? { email: formData.email, password: formData.password }
        : { 
            email: formData.email, 
            password: formData.password, 
            first_name: formData.first_name,
            last_name: formData.last_name,
            name: `${formData.first_name} ${formData.last_name}`.trim(),
            phone: formData.phone 
          };

      const response = await axios.post(`${API}${endpoint}`, payload, {
        withCredentials: true
      });

      if (response.data.user) {
        // Save token to localStorage for Safari/iOS compatibility
        if (response.data.token) {
          setAuthToken(response.data.token);
        }
        // Ensure new backend signature is persisted before redirect checks
        syncAuthBackendSignature();
        // Avoid login loop when mounted checkExistingSession races /auth/me
        setChecking(true);
        
        const user = response.data.user;
        sessionStorage.setItem('just_authenticated', 'true');
        
        if (user.role === 'admin') {
          navigate('/admin', { replace: true, state: { user } });
        } else if (user.status === 'approved') {
          navigate('/dashboard', { replace: true, state: { user } });
        } else if (!user.first_name || !user.last_name || !user.phone) {
          navigate('/complete-registration', { replace: true, state: { user } });
        } else {
          navigate('/pending-approval', { replace: true, state: { user } });
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      const errorMessage = err.response?.data?.detail || 'Une erreur est survenue. Vérifiez votre connexion internet.';
      
      // Provide helpful suggestions based on error
      if (errorMessage.includes('déjà utilisé')) {
        setError('Cet email est déjà utilisé. Essayez de vous connecter ou utilisez "Mot de passe oublié".');
      } else if (errorMessage.includes('incorrect')) {
        setError('Email ou mot de passe incorrect. Vérifiez vos identifiants ou utilisez "Mot de passe oublié".');
      } else if (errorMessage.includes('Google')) {
        setError('Ce compte utilise la connexion Google. Cliquez sur "Continuer avec Google" ci-dessus.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: formData.email
      });

      setSuccessMessage(response.data.message);
      setAuthMode('reset-password');
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        email: formData.email,
        reset_code: formData.resetCode,
        new_password: formData.newPassword
      });

      setSuccessMessage(response.data.message);
      // Reset form and go back to login
      setTimeout(() => {
        setAuthMode('login');
        setFormData({ ...formData, password: '', resetCode: '', newPassword: '' });
        setSuccessMessage('');
      }, 2000);
    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Forgot Password View
  if (authMode === 'forgot-password') {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={backgroundStyle}
        />
        <div className="absolute inset-0 bg-black/40" />
        
        <main className="relative z-10 container mx-auto px-4 sm:px-6 min-h-screen flex items-center justify-center py-8">
          <div 
            className="w-full max-w-md rounded-2xl shadow-2xl p-6 sm:p-10 border border-white/20"
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)'
            }}
          >
            <div className="text-center mb-6">
              <img src={LOGO_URL} alt="DLV EXPRESS" className="h-20 w-auto mx-auto mb-3" />
              <h2 className="text-xl font-bold text-[#0F172A]">Mot de passe oublié</h2>
              <p className="text-sm text-gray-600 mt-2">Entrez votre email pour recevoir un code de réinitialisation</p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  {successMessage}
                </div>
              )}

              <div className="space-y-1">
                <Label htmlFor="email" className="text-sm text-gray-700">Adresse email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="pl-10"
                    placeholder="votre@email.com"
                    required
                  />
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full btn-primary py-3">
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    <KeyRound className="w-5 h-5 mr-2" />
                    Envoyer le code
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => { setAuthMode('login'); setError(''); setSuccessMessage(''); }}
                className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-[#0F172A] mt-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à la connexion
              </button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  // Reset Password View
  if (authMode === 'reset-password') {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={backgroundStyle}
        />
        <div className="absolute inset-0 bg-black/40" />
        
        <main className="relative z-10 container mx-auto px-4 sm:px-6 min-h-screen flex items-center justify-center py-8">
          <div 
            className="w-full max-w-md rounded-2xl shadow-2xl p-6 sm:p-10 border border-white/20"
            style={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)'
            }}
          >
            <div className="text-center mb-6">
              <img src={LOGO_URL} alt="DLV EXPRESS" className="h-20 w-auto mx-auto mb-3" />
              <h2 className="text-xl font-bold text-[#0F172A]">Réinitialiser le mot de passe</h2>
              <p className="text-sm text-gray-600 mt-2">Entrez le code reçu et votre nouveau mot de passe</p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  {successMessage}
                </div>
              )}

              <div className="space-y-1">
                <Label htmlFor="resetCode" className="text-sm text-gray-700">Code de réinitialisation</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="resetCode"
                    type="text"
                    value={formData.resetCode}
                    onChange={(e) => setFormData({...formData, resetCode: e.target.value})}
                    className="pl-10 text-center tracking-widest font-mono text-lg"
                    placeholder="Code reçu par e-mail (12 caractères)"
                    maxLength={6}
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="newPassword" className="text-sm text-gray-700">Nouveau mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.newPassword}
                    onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                    className="pl-10 pr-10"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Minimum 6 caractères</p>
              </div>

              <Button type="submit" disabled={loading} className="w-full btn-primary py-3">
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : (
                  <>
                    Réinitialiser le mot de passe
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              <button
                type="button"
                onClick={() => { setAuthMode('login'); setError(''); setSuccessMessage(''); }}
                className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-[#0F172A] mt-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à la connexion
              </button>
            </form>
          </div>
        </main>
      </div>
    );
  }

  // Main Login/Register View
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image - responsive and optimized */}
      <div 
        className="absolute inset-0"
        style={backgroundStyle}
      />
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Back to Home Button */}
      <div className="absolute top-4 left-4 z-20">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-white/90 hover:bg-white text-[#0F172A] rounded-xl shadow-lg transition-all hover:scale-105 font-medium"
        >
          <ChevronLeft className="w-5 h-5" />
          Accueil
        </button>
      </div>
      
      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 sm:px-6 min-h-screen flex items-center justify-center py-8">
        <div 
          className="w-full max-w-md rounded-2xl shadow-2xl p-6 sm:p-10 border border-white/20"
          style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
          }}
        >
          {/* Logo */}
          <div className="text-center mb-6">
            <img src={LOGO_URL} alt="DLV EXPRESS" className="h-24 sm:h-32 w-auto mx-auto mb-3" />
            <p className="text-base sm:text-lg text-[#0F172A] font-medium italic">La maîtrise du dernier kilomètre</p>
          </div>

          {/* Google Login Button */}
          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 rounded-xl px-4 py-3 hover:bg-gray-50 hover:border-[#0F172A] transition-all duration-200 group shadow-md mb-4"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-gray-700 font-medium text-sm">Continuer avec Google</span>
          </button>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-gray-500">ou</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailAuth} className="space-y-4">
            {/* Mode Toggle */}
            <div className="flex rounded-lg bg-gray-100 p-1 mb-4">
              <button
                type="button"
                onClick={() => { setAuthMode('login'); setError(''); setSuccessMessage(''); }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  authMode === 'login' 
                    ? 'bg-white text-[#0F172A] shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Connexion
              </button>
              <button
                type="button"
                onClick={() => { setAuthMode('register'); setError(''); setSuccessMessage(''); }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                  authMode === 'register' 
                    ? 'bg-white text-[#0F172A] shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Inscription
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                {successMessage}
              </div>
            )}

            {/* Name fields (only for registration) */}
            {authMode === 'register' && (
              <>
                <div className="space-y-1">
                  <Label htmlFor="first_name" className="text-sm text-gray-700">Prénom <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="first_name"
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className="pl-10"
                      placeholder="Jean"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="last_name" className="text-sm text-gray-700">Nom de famille <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="last_name"
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className="pl-10"
                      placeholder="Dupont"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            {/* Email field */}
            <div className="space-y-1">
              <Label htmlFor="email" className="text-sm text-gray-700">Adresse email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="pl-10"
                  placeholder="votre@email.com"
                  required
                />
              </div>
            </div>

            {/* Password field */}
            <div className="space-y-1">
              <Label htmlFor="password" className="text-sm text-gray-700">Mot de passe</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="pl-10 pr-10"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="login-password-visibility-button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {authMode === 'register' && (
                <p className="text-xs text-gray-500">Minimum 6 caractères</p>
              )}
            </div>

            {/* Forgot Password Link (only for login) */}
            {authMode === 'login' && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => { setAuthMode('forgot-password'); setError(''); setSuccessMessage(''); }}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            )}

            {/* Phone field (only for registration) */}
            {authMode === 'register' && (
              <div className="space-y-1">
                <Label htmlFor="phone" className="text-sm text-gray-700">Téléphone <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="pl-10"
                    placeholder="06 12 34 56 78"
                    required
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="login-form-submit-button"
              disabled={loading}
              className="w-full btn-primary py-3"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  {authMode === 'login' ? 'Se connecter' : "S'inscrire"}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Footer text */}
          <p className="text-center text-xs text-gray-500 mt-6">
            En vous connectant, vous acceptez nos conditions d&apos;utilisation
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
